// src/v3/RingFinishSelections/index.tsx
import { useState } from "react";

// src/v3/RadioSelectorGroup/index.tsx
import {
  createContext
} from "react";
import { useRadioGroup } from "react-aria";
import { useRadioGroupState } from "react-stately";
import { cx } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
var RadioSelectorGroupContext = createContext(
  null
);
function RadioSelectorGroup(props) {
  const {
    cardSelectHeader: CardSelectHeader,
    children,
    hideLabel = false,
    label,
    description,
    infoModal,
    onChange: _onChange,
    ...rest
  } = props;
  const state = useRadioGroupState(props);
  const { radioGroupProps, labelProps, descriptionProps } = useRadioGroup(
    props,
    state
  );
  return /* @__PURE__ */ jsxs("div", {
    ...rest,
    ...radioGroupProps,
    children: [
      hideLabel ? /* @__PURE__ */ jsx("span", {
        ...labelProps,
        className: cx(labelProps.className, "ui-sr-only"),
        children: label
      }) : null,
      CardSelectHeader ? /* @__PURE__ */ jsx(CardSelectHeader, {
        description,
        descriptionProps,
        infoModal,
        label,
        labelProps
      }) : null,
      /* @__PURE__ */ jsx("div", {
        className: "ui-relative ui-flex ui-w-full ui-flex-col ui-gap-2",
        children: /* @__PURE__ */ jsx(RadioSelectorGroupContext.Provider, {
          value: state,
          children
        })
      })
    ]
  });
}
RadioSelectorGroup.displayName = "RadioSelectorGroup";

// src/v3/FinishRadioSelectors/FinishRadioSelector.tsx
import { useContext, useId, useRef } from "react";
import { useRadio } from "react-aria";

// src/v3/consts/stockStatus.ts
var OUT_OF_STOCK = "outOfStock";

// src/v3/FinishSwatch/index.tsx
import { cx as cx2 } from "class-variance-authority";

// src/icons/CloseXIcon.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function CloseXIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs2("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "currentColor",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx2("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx2("path", {
        d: "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
      })
    ]
  });
}

// src/v3/FinishSwatch/index.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var sizeClasses = {
  "extra-small": "ui-size-4 ui-aspect-square",
  small: "ui-size-6 ui-aspect-square",
  large: "ui-size-10 ui-aspect-square"
};
function FinishSwatch({
  className,
  borderClassName,
  isSelected,
  isDisabled,
  swatchColor,
  swatchSrc,
  size = "large"
}) {
  return /* @__PURE__ */ jsxs3("div", {
    className: cx2(
      "ui-aspect-square ui-rounded-full ui-flex ui-items-center ui-justify-center ui-relative ui-cursor-pointer",
      {
        "ui-size-12": size === "large",
        "ui-size-8": size === "small",
        "ui-size-4": size === "extra-small",
        "ui-border-sandstone-500 ui-border-2": isSelected && !isDisabled,
        "ui-pointer-events-none ui-cursor-default": isDisabled
      },
      borderClassName
    ),
    "data-cy": "finish-swatch-wrapper",
    children: [
      swatchSrc ? /* @__PURE__ */ jsx3("img", {
        alt: "",
        className: cx2(sizeClasses[size], className),
        src: swatchSrc
      }) : /* @__PURE__ */ jsx3("span", {
        className: cx2(sizeClasses[size], "ui-rounded-full", className),
        "data-cy": "finish-swatch-color",
        style: { backgroundColor: swatchColor }
      }),
      isDisabled ? /* @__PURE__ */ jsx3(CloseXIcon, {
        className: "ui-text-white ui-absolute ui-left-1/2 ui-transform ui-top-1/2 ui--translate-x-1/2 ui--translate-y-1/2",
        height: 24,
        width: 24
      }) : null
    ]
  });
}

// src/v3/FinishRadioSelectors/FinishRadioSelector.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function FinishRadioSelector(props) {
  const {
    finish,
    stockStatus,
    swatchSrc,
    swatchColor,
    value = "silver" /* Silver */
  } = props;
  const state = useContext(RadioSelectorGroupContext);
  const ref = useRef(null);
  const id = useId();
  const { inputProps, isSelected } = useRadio(
    props,
    state,
    ref
  );
  const isDisabled = stockStatus === OUT_OF_STOCK;
  return /* @__PURE__ */ jsxs4("label", {
    className: "ui-rounded-full",
    "data-cy": `finish-radio-selector-${value}`,
    "data-value": value,
    htmlFor: id,
    children: [
      /* @__PURE__ */ jsx4("span", {
        className: "ui-sr-only",
        children: finish
      }),
      /* @__PURE__ */ jsx4("input", {
        ...inputProps,
        checked: isSelected,
        className: "ui-peer ui-absolute ui-appearance-none",
        "data-cy": `finish-radio-selector-input-${value}`,
        disabled: isDisabled,
        id,
        ref,
        type: "radio"
      }),
      /* @__PURE__ */ jsx4(FinishSwatch, {
        isDisabled,
        isSelected,
        swatchColor,
        swatchSrc
      })
    ]
  });
}
FinishRadioSelector.displayName = "FinishRadioSelector";

// src/v3/FinishRadioSelectors/FinishRadioSelectors.tsx
import { cx as cx3 } from "class-variance-authority";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function FinishRadioSelectors({
  children,
  className,
  label,
  onChange,
  ringFinishesData,
  selectedFinish
}) {
  return /* @__PURE__ */ jsxs5(RadioSelectorGroup, {
    hideLabel: true,
    label,
    onChange,
    value: selectedFinish,
    children: [
      /* @__PURE__ */ jsx5("div", {
        className: cx3("ui-flex ui-gap-x-2", className),
        children: ringFinishesData.map((variant) => /* @__PURE__ */ jsx5(FinishRadioSelector, {
          "aria-label": variant.finish,
          finish: variant.finish,
          stockStatus: variant.stockStatus,
          swatchColor: variant.swatchColor,
          swatchSrc: variant.swatchSrc,
          value: variant.id
        }, variant.id))
      }),
      children
    ]
  });
}
FinishRadioSelectors.displayName = "FinishRadioSelectors";

// src/v3/Price/Price.tsx
import { cx as cx4 } from "class-variance-authority";
import { useContext as useContext2 } from "react";

// src/v3/Price/CurrencyProvider.tsx
import {
  I18nContext,
  I18nManager
} from "@shopify/react-i18n";
import { createContext as createContext2, useMemo, useRef as useRef2 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var CurrencyContext = createContext2({
  fallbackCurrencyCode: "USD",
  freeMessage: ""
});

// src/v3/Price/FormattedPrice.tsx
import { useI18n } from "@shopify/react-i18n";
import { Fragment, jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function FormattedPrice({
  amount,
  className = "",
  currencyCode,
  currencyCodeStyles = "",
  fractionDigits,
  showCurrency = false
}) {
  const [i18n] = useI18n();
  const formatted = i18n.formatCurrency(amount, {
    currency: currencyCode,
    form: showCurrency ? "explicit" : "short",
    precision: fractionDigits
  });
  const parts = formatted.split(" ");
  if (parts.length > 1 && currencyCodeStyles && showCurrency) {
    const [part, ...rest] = parts;
    return /* @__PURE__ */ jsxs6(Fragment, {
      children: [
        /* @__PURE__ */ jsxs6("span", {
          className,
          children: [
            part,
            "\xA0"
          ]
        }),
        /* @__PURE__ */ jsx7("span", {
          className: currencyCodeStyles,
          children: rest
        })
      ]
    });
  }
  return /* @__PURE__ */ jsx7(Fragment, {
    children: formatted
  });
}
FormattedPrice.displayName = "FormattedPrice";

// src/v3/Price/Price.tsx
import { jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
function Price({
  currencyCode,
  currencyCodeStyles,
  className,
  discount = 0,
  discountStyles,
  discountedPrice,
  price,
  priceCompare = false,
  stacked = false,
  showCurrency,
  showDecimalsForWholeDollars = false,
  showZeroAsFree = true,
  ...props
}) {
  const { fallbackCurrencyCode, freeMessage } = useContext2(CurrencyContext);
  const selectedCurrencyCode = currencyCode != null ? currencyCode : fallbackCurrencyCode;
  const parsedPrice = Number(price);
  const _discountedPrice = discountedPrice ? { ...discountedPrice } : {
    amount: Math.max(parsedPrice - discount, 0),
    currencyCode: selectedCurrencyCode
  };
  const isFree = showZeroAsFree && freeMessage && _discountedPrice.amount === 0;
  const basePriceFractionDigits = showDecimalsForWholeDollars || parsedPrice % 1 !== 0 ? 2 : 0;
  const discountedPriceFractionDigits = showDecimalsForWholeDollars || _discountedPrice.amount % 1 !== 0 ? 2 : 0;
  const comparingPrices = priceCompare && parsedPrice - _discountedPrice.amount > 0;
  return /* @__PURE__ */ jsxs7("span", {
    className: cx4(className, {
      block: priceCompare && stacked,
      "ui-inline-flex ui-gap-2": priceCompare && !stacked
    }),
    "data-cy": "sormus-price",
    ...props,
    children: [
      /* @__PURE__ */ jsx8("span", {
        "data-cy": discount && priceCompare ? "product-detail-discount-price" : "product-detail-price",
        children: isFree ? freeMessage : /* @__PURE__ */ jsx8(FormattedPrice, {
          amount: _discountedPrice.amount,
          currencyCode: _discountedPrice.currencyCode,
          currencyCodeStyles,
          fractionDigits: discountedPriceFractionDigits,
          showCurrency
        })
      }),
      comparingPrices && !currencyCodeStyles ? /* @__PURE__ */ jsx8("del", {
        className: cx4("ui-line-through", discountStyles, {
          "ui-block": stacked
        }),
        "data-cy": "product-detail-regular-price",
        children: /* @__PURE__ */ jsx8(FormattedPrice, {
          amount: parsedPrice,
          currencyCode: selectedCurrencyCode,
          currencyCodeStyles,
          fractionDigits: basePriceFractionDigits,
          showCurrency
        })
      }) : null,
      comparingPrices && currencyCodeStyles ? /* @__PURE__ */ jsx8("span", {
        className: cx4(discountStyles, {
          "ui-block": stacked
        }),
        "data-cy": "product-detail-regular-price",
        children: /* @__PURE__ */ jsx8(FormattedPrice, {
          amount: parsedPrice,
          className: "ui-line-through",
          currencyCode: selectedCurrencyCode,
          currencyCodeStyles,
          fractionDigits: basePriceFractionDigits,
          showCurrency
        })
      }) : null
    ]
  });
}
Price.displayName = "Price";

// src/v3/Typography/index.tsx
import { cva } from "class-variance-authority";
import { jsx as jsx9 } from "react/jsx-runtime";
var typography = cva("", {
  variants: {
    variant: {
      h1: "ui-text-[2.5rem] ui-tracking-tight md:ui-text-[3.75rem] lg:ui-text-[5rem]",
      h2: "ui-text-[2.25rem] ui-tracking-normal md:ui-text-[3rem] lg:ui-text-[4.25rem] lg:ui-tracking-[-2px]",
      h3: "ui-text-[2rem] ui-tracking-normal md:ui-text-[2.5rem] lg:ui-text-[3.5rem]",
      h4: "ui-text-[1.75rem] ui-tracking-normal md:ui-text-[2rem] lg:ui-text-[2.5rem]",
      h5: "ui-text-[1.25rem] ui-tracking-normal lg:ui-leading-10",
      h6: "ui-text-[1.25rem] ui-tracking-normal",
      "body-large": "ui-text-[1.125rem] ui-tracking-normal",
      "title-large": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-8xl xl:ui-text-[7.5rem]",
      "title-small": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-[5rem]",
      base: "ui-text-base ui-tracking-normal",
      "body-small": "ui-text-[0.875rem] ui-tracking-normal",
      "eyebrow-large": "ui-text-base",
      "eyebrow-small": "ui-text-[0.75rem]",
      unset: ""
    },
    align: {
      left: "ui-text-left",
      center: "ui-text-center",
      right: "ui-text-right",
      justify: "ui-text-justify",
      centerMdLeft: "ui-text-center md:ui-text-left",
      centerLgLeft: "ui-text-center lg:ui-text-left"
    },
    color: {
      light: "ui-text-sandstone-200",
      dark: "ui-text-sandstone-500",
      disabled: "ui-text-gray-300",
      error: "ui-text-red-600",
      gray: "ui-text-gray-400",
      success: "ui-text-green-600",
      inherit: "ui-text-inherit",
      unset: ""
    },
    font: {
      sans: "ui-font-sans",
      serif: "ui-font-serif",
      "serif-italic": "ui-font-serif ui-italic"
    },
    height: {
      none: "ui-leading-none",
      tight: "ui-leading-tight",
      snug: "ui-leading-snug",
      normal: "ui-leading-normal",
      relaxed: "ui-leading-relaxed",
      loose: "ui-leading-loose",
      heading: "ui-leading-heading"
    },
    weight: {
      thin: "ui-font-thin",
      extralight: "ui-font-extralight",
      light: "ui-font-light",
      normal: "ui-font-normal",
      medium: "ui-font-medium",
      semibold: "ui-font-semibold",
      bold: "ui-font-bold",
      extrabold: "ui-font-extrabold",
      black: "ui-font-black"
    }
  },
  defaultVariants: {
    variant: "base",
    height: "normal",
    weight: "normal",
    font: "sans"
  }
});
function Typography({
  align = "left",
  Element = "p",
  children,
  className,
  color = "dark",
  font = "sans",
  height,
  variant = "base",
  weight = "normal",
  style,
  ...props
}) {
  let _weight = weight;
  if (variant === "h1" || variant === "h2" || variant === "h3" || variant === "h4") {
    _weight = "light";
  }
  return /* @__PURE__ */ jsx9(Element, {
    className: typography({
      align,
      color,
      height,
      variant,
      weight: _weight,
      font,
      className
    }),
    style,
    ...props,
    children
  });
}
Typography.displayName = "Typography";

// src/v3/RingFinishSelections/index.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
function RingFinishSelections({
  currencyCode,
  initialSelectedFinish = "silver" /* Silver */,
  label,
  onChange,
  ringFinishesData
}) {
  var _a, _b;
  const [selectedFinish, setSelectedFinish] = useState(
    initialSelectedFinish
  );
  const selectedItem = ringFinishesData.find(
    (item) => item.id === selectedFinish
  );
  const finish = (_a = selectedItem == null ? void 0 : selectedItem.finish) != null ? _a : "";
  const price = selectedItem == null ? void 0 : selectedItem.price;
  const discount = (_b = selectedItem == null ? void 0 : selectedItem.discount) != null ? _b : 0;
  const stockStatus = selectedItem == null ? void 0 : selectedItem.stockStatus;
  const handleChange = (val) => {
    setSelectedFinish(val);
    onChange == null ? void 0 : onChange(val);
  };
  return /* @__PURE__ */ jsx10(FinishRadioSelectors, {
    label,
    onChange: handleChange,
    ringFinishesData,
    selectedFinish,
    children: /* @__PURE__ */ jsxs8("div", {
      className: "ui-flex ui-items-center",
      children: [
        /* @__PURE__ */ jsxs8(Typography, {
          Element: "span",
          color: "gray",
          "data-cy": "ring-finish",
          children: [
            finish,
            "\xA0 \u2013 \xA0"
          ]
        }),
        stockStatus !== OUT_OF_STOCK && price ? /* @__PURE__ */ jsx10(Price, {
          className: "ui-text-gray-400 ui-text-sm md:ui-text-base",
          currencyCode,
          discount,
          price,
          priceCompare: true,
          showZeroAsFree: true
        }) : null
      ]
    })
  });
}
RingFinishSelections.displayName = "RingFinishSelections";
export {
  RingFinishSelections
};
//# sourceMappingURL=index.mjs.map